import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { FiChevronDown, FiLogOut, FiMapPin } from "react-icons/fi";
import axios from "../api/axiosInstance";
import useIsMobile from "../hooks/useIsMobile";
import { readStoredUser } from "../utils/sessionUser";
import { useCrmCopy } from "./crmI18n";
import { buildCrmNavigation } from "./crmNavigation";
import ZadarmaWebRtcWidget from "./ZadarmaWebRtcWidget";

export default function CrmPanel() {
  const location = useLocation();
  const isMobile = useIsMobile();
  const copy = useCrmCopy();
  const [sessionUser, setSessionUser] = useState(() => readStoredUser());
  const [businessUnitName, setBusinessUnitName] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarPinned, setIsSidebarPinned] = useState(false);
  const [openSection, setOpenSection] = useState(() =>
    typeof window !== "undefined" && window.location.pathname.startsWith("/crm/settings")
      ? "settings"
      : null
  );

  const navItems = useMemo(() => buildCrmNavigation(copy), [copy]);
  const activeBusinessUnitId =
    sessionUser?.active_business_unit_id || sessionUser?.business_unit_ids?.[0] || "";
  const businessUnitLabel =
    businessUnitName || sessionUser?.business_unit_name || copy.common.businessUnit;
  const isSidebarExpanded = isMobile || isSidebarPinned || isSidebarOpen;

  useEffect(() => {
    const syncSessionFromStorage = () => {
      setSessionUser(readStoredUser());
    };

    window.addEventListener("palantir-session-updated", syncSessionFromStorage);
    return () => {
      window.removeEventListener("palantir-session-updated", syncSessionFromStorage);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadBusinessUnitName = async () => {
      if (!activeBusinessUnitId) {
        setBusinessUnitName("");
        return;
      }

      try {
        const res = await axios.get(`/api/business-units/${activeBusinessUnitId}`);
        if (!cancelled) {
          setBusinessUnitName(String(res?.data?.name || "").trim());
        }
      } catch (err) {
        if (!cancelled) {
          setBusinessUnitName("");
        }
        console.warn("[CrmPanel] Error loading business unit name:", err);
      }
    };

    loadBusinessUnitName();

    return () => {
      cancelled = true;
    };
  }, [activeBusinessUnitId]);

  useEffect(() => {
    if (location.pathname.startsWith("/crm/settings")) {
      setOpenSection("settings");
    }
  }, [location.pathname]);

  const handleSidebarEnter = () => {
    if (isMobile) return;
    setIsSidebarOpen(true);
  };

  const handleSidebarLeave = () => {
    if (isMobile || isSidebarPinned) return;
    setIsSidebarOpen(false);
  };

  const handlePinToggle = () => {
    if (isMobile) return;
    const next = !isSidebarPinned;
    setIsSidebarPinned(next);
    setIsSidebarOpen(next);
  };

  const handleLogout = useCallback(() => {
    localStorage.removeItem("palantir_token");
    localStorage.removeItem("palantir_user");
    window.location.href = "/";
  }, []);

  const cleanPath = (value) => String(value || "").split("?")[0];
  const isPathActive = (to) => {
    const path = cleanPath(to);
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const isNodeActive = (node) => {
    if (node.path) return isPathActive(node.path);
    if (node.activePrefix) return location.pathname.startsWith(node.activePrefix);
    if (node.children?.length) return node.children.some((child) => isNodeActive(child));
    return false;
  };

  return (
    <div className="crm-shell">
      <aside
        className={`crm-sidebar ${isSidebarExpanded ? "crm-sidebar--open" : ""} ${
          isSidebarPinned ? "crm-sidebar--pinned" : ""
        }`}
        aria-label={copy.shell.navigationLabel}
        onMouseEnter={handleSidebarEnter}
        onMouseLeave={handleSidebarLeave}
      >
        <div className="crm-sidebarInner">
          <div className="crm-brand" title={copy.common.crm}>
            <div className="crm-brandMark">
              <img
                className="crm-brandLogo"
                src={`${process.env.PUBLIC_URL}/Isotipo_Pilcore_Blanco.svg`}
                alt="Pilcore"
              />
            </div>
            <div className="crm-brandText">
              <div className="crm-brandTitle">{copy.common.crm}</div>
              <div className="crm-brandSubtitle">{copy.common.pilcore}</div>
              <div className="crm-brandHotel" title={businessUnitLabel}>
                {businessUnitLabel}
              </div>
              <div className="crm-brandActions">
                <span className="crm-brandLogoutIcon" aria-hidden="true">
                  <FiLogOut size={11} />
                </span>
                <button
                  type="button"
                  className="crm-brandLogout"
                  onClick={handleLogout}
                  title={copy.common.logout}
                >
                  {copy.common.logout}
                </button>
              </div>
            </div>
            {isSidebarExpanded && !isMobile ? (
              <button
                type="button"
                className={`crm-pinButton ${isSidebarPinned ? "crm-pinButton--active" : ""}`}
                onClick={handlePinToggle}
                title={isSidebarPinned ? copy.shell.unpinSidebar : copy.shell.pinSidebar}
                aria-pressed={isSidebarPinned}
                aria-label={isSidebarPinned ? copy.shell.unpinSidebar : copy.shell.pinSidebar}
              >
                <FiMapPin size={14} />
              </button>
            ) : null}
          </div>

          <nav className="crm-nav">
            {navItems.map(({ key, label, meta, path, Icon, children, activePrefix }) => {
              if (children?.length) {
                const isActive = isNodeActive({ activePrefix, children });
                const isOpen = openSection === key;

                return (
                  <div key={key} className="crm-navSection">
                    <button
                      type="button"
                      className={`crm-navItem ${isActive ? "crm-navItem--active" : ""}`}
                      aria-expanded={isOpen}
                      onClick={() => setOpenSection((prev) => (prev === key ? null : key))}
                      title={label}
                    >
                      <span className="crm-navItemIcon" aria-hidden="true">
                        <Icon size={18} />
                      </span>
                      <span className="crm-navItemText">
                        <span className="crm-navItemLabel">{label}</span>
                        <span className="crm-navItemMeta">{meta}</span>
                      </span>
                      <span className="crm-chevron" aria-hidden="true">
                        <FiChevronDown size={16} />
                      </span>
                    </button>

                    <div className={`crm-subNav ${isOpen ? "crm-subNav--open" : ""}`}>
                      {children.map((child) => (
                        <NavLink
                          key={child.key}
                          to={child.path}
                          title={child.label}
                          className={({ isActive: childActive }) =>
                            `crm-subItem ${childActive ? "crm-subItem--active" : ""}`
                          }
                        >
                          {child.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <NavLink
                  key={key}
                  to={path}
                  title={label}
                  className={({ isActive }) =>
                    `crm-navItem ${isActive ? "crm-navItem--active" : ""}`
                  }
                >
                  <span className="crm-navItemIcon" aria-hidden="true">
                    <Icon size={18} />
                  </span>
                  <span className="crm-navItemText">
                    <span className="crm-navItemLabel">{label}</span>
                    <span className="crm-navItemMeta">{meta}</span>
                  </span>
                </NavLink>
              );
            })}
          </nav>
        </div>
      </aside>

      <main className="crm-main">
        <Outlet />
      </main>
      <ZadarmaWebRtcWidget businessUnitId={activeBusinessUnitId} />
    </div>
  );
}
